/**
 * Helper utilities for working with the .aus selection file format.
 */

/**
 * Strips leading and trailing single (') or double (") quotes from a string.
 * E.g., "'Workflow.id'" -> "Workflow.id"
 * E.g., "SYSTEM.GUI.SubTab.'1'.'1'" -> "SYSTEM.GUI.SubTab.'1'.'1'" (does not strip internal quotes)
 */
export const stripQuotes = (str) => {
  if (!str) return '';
  if (
    (str.startsWith("'") && str.endsWith("'")) ||
    (str.startsWith('"') && str.endsWith('"'))
  ) {
    return str.slice(1, -1);
  }
  return str;
};

/**
 * Parses .aus file content into a multi-value selections object.
 * Format:
 *   variableA=valueX;
 *   variableB=valueY,valueZ;
 * 
 * Returns: An object mapping variable names to an array of parsed values:
 *   {
 *     "variableA": ["valueX"],
 *     "variableB": ["valueY", "valueZ"]
 *   }
 */
export const parseAusFile = (text) => {
  const result = {};
  if (!text) return result;

  const lines = text.split(/\r?\n/);
  for (let line of lines) {
    line = line.trim();
    // Skip empty lines or comment lines
    if (!line || line.startsWith('#') || line.startsWith('//')) {
      continue;
    }

    // Strip trailing semicolon if present
    if (line.endsWith(';')) {
      line = line.slice(0, -1).trim();
    }

    const eqIndex = line.indexOf('=');
    if (eqIndex === -1) {
      continue;
    }

    const rawVar = line.slice(0, eqIndex).trim();
    const rawValPart = line.slice(eqIndex + 1).trim();

    const varName = stripQuotes(rawVar);
    if (!varName) continue;

    // Split multiple values by comma
    const values = rawValPart.split(',').map(val => stripQuotes(val.trim())).filter(Boolean);

    if (values.length > 0) {
      result[varName] = values;
    }
  }

  return result;
};

/**
 * Exports the current selections dictionary into .aus file format content.
 * Format:
 *   variableA=valueX;
 *   variableB=valueY;
 * 
 * Selections is expected to be a dictionary: { [varName]: value }
 */
export const exportAusFile = (selections) => {
  if (!selections) return '';
  
  let output = '';
  for (const [varName, value] of Object.entries(selections)) {
    if (!value) continue;
    // Strip surrounding quotes just in case, to maintain clean export format
    const cleanVar = stripQuotes(varName);
    const cleanVal = stripQuotes(value);
    
    output += `${cleanVar}=${cleanVal};\n`;
  }
  return output;
};
